(() => {
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const MENU_DURATION = 340;

  const runWhenReady = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const getHeaderOffset = () => {
    const header = document.querySelector('.vvop-header');
    return Math.max(0, Math.round(header?.getBoundingClientRect().height || 58));
  };

  const resolveAnchor = (hash) => {
    const aliases = {
      '#buy-now': '#buy-card',
      '#shop': '#buy-card',
      '#how-steps': '#how-it-works'
    };
    return aliases[hash] || hash;
  };

  const getAnchorGap = (target) => {
    if (target.matches('#buy-card')) return 5;
    if (target.matches('#how-it-works, #how-steps')) return 6;
    return 6;
  };

  const getVisualAnchor = (target) => {
    if (target.matches('#how-it-works')) {
      return target.querySelector('.vvop-section-head') || target;
    }
    return target;
  };

  const scrollToAnchor = (target, updateHash = true) => {
    if (!target) return;
    const visualTarget = getVisualAnchor(target);
    const targetTop = visualTarget.getBoundingClientRect().top + window.scrollY;
    const top = Math.max(0, targetTop - getHeaderOffset() - getAnchorGap(target));
    window.scrollTo({ top, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    if (updateHash && target.id) {
      try { history.replaceState(null, '', `#${target.id}`); } catch (error) { /* no-op */ }
    }
  };

  const closeAllMenus = () => {
    document.querySelectorAll('[data-vv-mobile-menu].is-open').forEach((menu) => {
      menu.dispatchEvent(new CustomEvent('vv:menu-close'));
    });
  };

  const initSmartAnchors = () => {
    if (document.documentElement.dataset.vvAnchorsReady === 'true') return;
    document.documentElement.dataset.vvAnchorsReady = 'true';

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href*="#"]');
      if (!link) return;

      let url;
      try { url = new URL(link.href, window.location.href); } catch (error) { return; }
      if (!url.hash || url.origin !== window.location.origin) return;

      const mappedHash = resolveAnchor(url.hash);
      const target = document.querySelector(mappedHash);
      if (!target) return;

      event.preventDefault();
      closeAllMenus();
      window.setTimeout(() => scrollToAnchor(target), 40);
    });

    if (window.location.hash) {
      const target = document.querySelector(resolveAnchor(window.location.hash));
      if (target) window.setTimeout(() => scrollToAnchor(target, false), 110);
    }
  };

  const initMenus = (scope = document) => {
    scope.querySelectorAll('[data-vv-mobile-menu]:not([data-vv-menu-ready])').forEach((menu) => {
      menu.dataset.vvMenuReady = 'true';
      const summary = menu.querySelector('summary');
      const panel = menu.querySelector('.vvop-mobile-menu__panel');
      const closeButton = menu.querySelector('.vvop-mobile-menu__top [data-vv-menu-close]');
      let lastFocused = null;
      let closeTimer = null;

      const finishClose = () => {
        window.clearTimeout(closeTimer);
        menu.removeAttribute('open');
        menu.classList.remove('is-closing');
        document.body.classList.remove('vv-menu-open');
        if (lastFocused instanceof HTMLElement) lastFocused.focus({ preventScroll: true });
      };

      const close = (immediate = false) => {
        if (!menu.hasAttribute('open')) return;
        window.clearTimeout(closeTimer);
        menu.classList.remove('is-open');
        menu.classList.add('is-closing');
        document.body.classList.remove('vv-menu-open');
        if (immediate || reduceMotion.matches) {
          finishClose();
        } else {
          closeTimer = window.setTimeout(finishClose, MENU_DURATION);
        }
      };

      const open = () => {
        window.clearTimeout(closeTimer);
        lastFocused = document.activeElement;
        menu.classList.remove('is-closing');
        menu.setAttribute('open', '');
        document.body.classList.add('vv-menu-open');
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => menu.classList.add('is-open'));
        });
        window.setTimeout(() => closeButton?.focus({ preventScroll: true }), reduceMotion.matches ? 0 : MENU_DURATION);
      };

      summary?.addEventListener('click', (event) => {
        event.preventDefault();
        menu.classList.contains('is-open') ? close() : open();
      });

      menu.addEventListener('vv:menu-close', () => close());
      menu.querySelectorAll('[data-vv-menu-close]').forEach((button) => button.addEventListener('click', () => close()));
      menu.querySelectorAll('nav a, .vvop-mobile-menu__cta').forEach((link) => link.addEventListener('click', () => close()));

      panel?.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close();
      });
    });

    if (document.documentElement.dataset.vvMenuEscapeReady !== 'true') {
      document.documentElement.dataset.vvMenuEscapeReady = 'true';
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeAllMenus();
      });
    }
  };

  const initGalleries = (scope = document) => {
    scope.querySelectorAll('[data-vv-simple-gallery]:not([data-vv-gallery-ready])').forEach((gallery) => {
      gallery.dataset.vvGalleryReady = 'true';
      const mainImage = gallery.querySelector('[data-vv-gallery-main]');
      const mainFrame = gallery.querySelector('.vvmb-gallery__main, .vvpg-mobile__main');
      const previous = gallery.querySelector('[data-vv-gallery-prev]');
      const next = gallery.querySelector('[data-vv-gallery-next]');
      const counter = gallery.querySelector('[data-vv-gallery-counter]');
      const modal = gallery.querySelector('[data-vv-gallery-modal]');
      const modalPanel = modal?.querySelector('.vv-gallery-modal__panel');
      const closeButton = modal?.querySelector('.vv-gallery-modal__close');
      const visibleThumbs = Array.from(gallery.querySelectorAll('[data-vv-gallery-thumb]'));
      const modalItems = Array.from(gallery.querySelectorAll('[data-vv-gallery-modal-item]'));
      const moreThumb = gallery.querySelector('[data-vv-gallery-open]');
      let lastFocused = null;
      let touchStartX = 0;
      let touchStartY = 0;

      const rawSources = modalItems.length ? modalItems : visibleThumbs;
      const sources = [];
      const seen = new Set();
      rawSources.forEach((button) => {
        const src = button?.dataset?.imageSrc;
        if (!src || seen.has(src)) return;
        seen.add(src);
        sources.push(button);
      });

      let currentIndex = Math.max(0, sources.findIndex((button) => button.dataset.imageSrc === mainImage?.getAttribute('src')));
      if (currentIndex < 0) currentIndex = 0;

      const updateCounter = () => {
        if (counter) counter.textContent = `${Math.min(currentIndex + 1, Math.max(1, sources.length))} / ${Math.max(1, sources.length)}`;
      };

      const updateActiveThumb = (src) => {
        let visibleActive = false;
        visibleThumbs.forEach((thumb) => {
          const active = thumb.dataset.imageSrc === src;
          thumb.classList.toggle('is-active', active);
          thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
          if (active) visibleActive = true;
        });
        if (moreThumb) {
          moreThumb.classList.toggle('is-active', !visibleActive && currentIndex >= visibleThumbs.length);
          moreThumb.setAttribute('aria-current', !visibleActive && currentIndex >= visibleThumbs.length ? 'true' : 'false');
        }
      };

      let transitionId = 0;

      const preloadImage = (src) => new Promise((resolve) => {
        const image = new Image();
        const done = () => resolve();
        image.addEventListener('load', done, { once: true });
        image.addEventListener('error', done, { once: true });
        image.src = src;
        if (image.complete) resolve();
      });

      const applyImage = (button) => {
        if (!mainImage || !button?.dataset.imageSrc) return;
        const src = button.dataset.imageSrc;
        mainImage.removeAttribute('srcset');
        mainImage.src = src;
        mainImage.alt = button.dataset.imageAlt || mainImage.alt || '';
        updateActiveThumb(src);
        updateCounter();
      };

      const selectImage = async (button, direction = 0, animate = true) => {
        if (!mainImage || !button?.dataset.imageSrc) return;
        const src = button.dataset.imageSrc;
        const foundIndex = sources.findIndex((source) => source.dataset.imageSrc === src);
        if (foundIndex >= 0) currentIndex = foundIndex;

        const currentSrc = mainImage.getAttribute('src') || '';
        if (currentSrc === src) {
          updateActiveThumb(src);
          updateCounter();
          return;
        }

        const token = ++transitionId;
        const shouldAnimate = animate && mainFrame && !reduceMotion.matches && typeof mainImage.animate === 'function';
        if (!shouldAnimate) {
          applyImage(button);
          return;
        }

        await preloadImage(src);
        if (token !== transitionId) return;

        const slideDistance = Math.max(22, Math.min(46, (mainFrame.clientWidth || 360) * 0.085));
        const sign = direction === 0 ? 0 : Math.sign(direction);
        const outgoingX = sign === 0 ? 0 : -sign * slideDistance;
        const incomingX = sign === 0 ? 0 : sign * slideDistance;

        mainFrame.classList.add('is-transitioning');
        const outgoing = mainImage.animate([
          { opacity: 1, transform: 'translate3d(0,0,0) scale(1)' },
          { opacity: 0, transform: `translate3d(${outgoingX}px,0,0) scale(.992)` }
        ], {
          duration: 135,
          easing: 'cubic-bezier(.4,0,1,1)',
          fill: 'forwards'
        });

        try { await outgoing.finished; } catch (_) {}
        if (token !== transitionId) {
          outgoing.cancel();
          mainFrame.classList.remove('is-transitioning');
          return;
        }

        mainImage.style.opacity = '0';
        mainImage.style.transform = `translate3d(${incomingX}px,0,0) scale(.992)`;
        outgoing.cancel();
        applyImage(button);
        try { await mainImage.decode(); } catch (_) {}
        if (token !== transitionId) return;

        const incoming = mainImage.animate([
          { opacity: 0, transform: `translate3d(${incomingX}px,0,0) scale(.992)` },
          { opacity: 1, transform: 'translate3d(0,0,0) scale(1)' }
        ], {
          duration: 210,
          easing: 'cubic-bezier(.22,.61,.36,1)',
          fill: 'forwards'
        });

        try { await incoming.finished; } catch (_) {}
        if (token === transitionId) {
          incoming.cancel();
          mainImage.style.removeProperty('opacity');
          mainImage.style.removeProperty('transform');
          mainFrame.classList.remove('is-transitioning');
        }
      };

      const step = (direction) => {
        if (sources.length < 2) return;
        const targetIndex = (currentIndex + direction + sources.length) % sources.length;
        selectImage(sources[targetIndex], direction);
      };

      const openModal = (trigger) => {
        if (!modal) return;
        lastFocused = trigger || document.activeElement;
        modal.hidden = false;
        document.body.classList.add('vv-gallery-open');
        window.requestAnimationFrame(() => (closeButton || modalPanel)?.focus({ preventScroll: true }));
      };

      const closeModal = () => {
        if (!modal) return;
        modal.hidden = true;
        document.body.classList.remove('vv-gallery-open');
        if (lastFocused instanceof HTMLElement) lastFocused.focus({ preventScroll: true });
      };

      visibleThumbs.forEach((button) => button.addEventListener('click', () => {
        const targetIndex = sources.findIndex((source) => source.dataset.imageSrc === button.dataset.imageSrc);
        const direction = targetIndex === currentIndex ? 0 : (targetIndex > currentIndex ? 1 : -1);
        selectImage(button, direction);
      }));
      gallery.querySelectorAll('[data-vv-gallery-open]').forEach((button) => button.addEventListener('click', () => openModal(button)));
      gallery.querySelectorAll('[data-vv-gallery-close]').forEach((button) => button.addEventListener('click', closeModal));
      modalItems.forEach((button) => {
        button.addEventListener('click', () => {
          selectImage(button, 0);
          closeModal();
          window.requestAnimationFrame(() => mainImage?.scrollIntoView({ block: 'nearest' }));
        });
      });
      previous?.addEventListener('click', () => step(-1));
      next?.addEventListener('click', () => step(1));
      modal?.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeModal();
      });

      if (mainFrame && mainImage && sources.length > 1) {
        mainImage.draggable = false;
        mainFrame.addEventListener('touchstart', (event) => {
          const touch = event.touches?.[0];
          if (!touch) return;
          touchStartX = touch.clientX;
          touchStartY = touch.clientY;
        }, { passive: true });
        mainFrame.addEventListener('touchend', (event) => {
          if (!mobileQuery.matches) return;
          const touch = event.changedTouches?.[0];
          if (!touch) return;
          const dx = touch.clientX - touchStartX;
          const dy = touch.clientY - touchStartY;
          if (Math.abs(dx) < 38 || Math.abs(dx) < Math.abs(dy) * 1.15) return;
          step(dx < 0 ? 1 : -1);
        }, { passive: true });
      }

      if (sources.length) {
        const mainSrc = mainImage?.getAttribute('src') || '';
        const matching = sources.findIndex((button) => button.dataset.imageSrc === mainSrc);
        currentIndex = matching >= 0 ? matching : 0;
        updateActiveThumb(sources[currentIndex]?.dataset.imageSrc || mainSrc);
        updateCounter();
      }

      const sourceGallery = gallery.nextElementSibling?.matches('media-gallery') ? gallery.nextElementSibling : null;
      if (sourceGallery && mainImage && 'MutationObserver' in window) {
        let previousSrc = '';
        const syncFromThemeGallery = () => {
          const active = sourceGallery.querySelector('.product__media-item.is-active img') || sourceGallery.querySelector('.product__media-item img');
          const source = active?.currentSrc || active?.src;
          if (!source || source === previousSrc) return;
          previousSrc = source;
          mainImage.removeAttribute('srcset');
          mainImage.src = source;
          if (active.alt) mainImage.alt = active.alt;
        };
        new MutationObserver(syncFromThemeGallery).observe(sourceGallery, { subtree: true, attributes: true, attributeFilter: ['class', 'src', 'srcset'] });
      }
    });
  };

  const initCarousels = (scope = document) => {
    scope.querySelectorAll('[data-vv-carousel]:not([data-vv-carousel-ready])').forEach((carousel) => {
      carousel.dataset.vvCarouselReady = 'true';
      const track = carousel.querySelector('[data-vv-carousel-track]');
      const slides = Array.from(carousel.querySelectorAll('[data-vv-slide]'));
      const dots = Array.from(carousel.querySelectorAll('[data-vv-carousel-dot]'));
      const previous = carousel.querySelector('[data-vv-carousel-prev]');
      const next = carousel.querySelector('[data-vv-carousel-next]');
      const interval = Math.max(3000, Number(carousel.dataset.vvInterval) || 4500);
      const desktopWindow = Math.max(1, Number(carousel.dataset.vvDesktopWindow) || 4);
      const desktopRotate = carousel.dataset.vvDesktopRotate === 'true';
      let index = 0;
      let timer = null;
      let resizeTimer = null;

      const measure = () => {
        if (!track) return;
        if (!mobileQuery.matches) {
          track.style.removeProperty('--vv-carousel-height');
          track.style.removeProperty('height');
          track.style.removeProperty('min-height');
          return;
        }

        const width = Math.round(track.getBoundingClientRect().width);
        if (width < 40) return;
        const measurer = document.createElement('div');
        measurer.className = 'vv-carousel-measurer';
        measurer.style.cssText = `position:absolute;left:-99999px;top:0;width:${width}px;visibility:hidden;pointer-events:none;contain:layout style;`;
        document.body.appendChild(measurer);

        let maxHeight = 0;
        slides.forEach((slide) => {
          const clone = slide.cloneNode(true);
          clone.removeAttribute('aria-hidden');
          clone.classList.add('is-active');
          clone.style.cssText = 'position:static!important;width:100%!important;max-width:none!important;height:auto!important;min-height:0!important;transform:none!important;opacity:1!important;';
          const displayMode = slide.classList.contains('ve-article') || slide.classList.contains('vvop-step') ? 'grid' : 'flex';
          clone.style.setProperty('display', displayMode, 'important');
          measurer.appendChild(clone);
          maxHeight = Math.max(maxHeight, Math.ceil(clone.getBoundingClientRect().height));
          clone.remove();
        });
        measurer.remove();

        if (maxHeight > 0) {
          track.style.setProperty('--vv-carousel-height', `${maxHeight}px`);
          track.style.minHeight = `${maxHeight}px`;
          track.style.removeProperty('height');
        }
      };

      const render = () => {
        const mobile = mobileQuery.matches;
        const visibleDesktopIndexes = new Map();
        if (!mobile && desktopRotate) {
          const count = Math.min(desktopWindow, slides.length);
          for (let offset = 0; offset < count; offset += 1) {
            visibleDesktopIndexes.set((index + offset) % slides.length, offset);
          }
        }

        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === index;
          const desktopVisible = !mobile && (!desktopRotate || visibleDesktopIndexes.has(slideIndex));
          slide.classList.toggle('is-active', mobile && active);
          slide.classList.toggle('is-desktop-visible', desktopVisible);
          slide.style.order = !mobile && desktopRotate && desktopVisible ? String(visibleDesktopIndexes.get(slideIndex)) : '';
          slide.setAttribute('aria-hidden', mobile ? (!active ? 'true' : 'false') : (desktopVisible ? 'false' : 'true'));
        });
        dots.forEach((dot, dotIndex) => {
          const active = dotIndex === index;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-selected', active ? 'true' : 'false');
        });
      };

      const stop = () => {
        if (timer) window.clearInterval(timer);
        timer = null;
      };

      const start = () => {
        stop();
        if (reduceMotion.matches || slides.length < 2 || document.hidden) return;
        const shouldRotateMobile = mobileQuery.matches;
        const shouldRotateDesktop = !mobileQuery.matches && desktopRotate && slides.length > desktopWindow;
        if (!shouldRotateMobile && !shouldRotateDesktop) return;
        timer = window.setInterval(() => {
          index = (index + 1) % slides.length;
          render();
        }, interval);
      };

      const goTo = (newIndex) => {
        index = (newIndex + slides.length) % slides.length;
        render();
        start();
      };

      previous?.addEventListener('click', () => goTo(index - 1));
      next?.addEventListener('click', () => goTo(index + 1));
      dots.forEach((dot) => dot.addEventListener('click', () => goTo(Number(dot.dataset.index) || 0)));
      carousel.addEventListener('focusin', stop);
      carousel.addEventListener('focusout', () => window.setTimeout(start, 100));
      carousel.addEventListener('pointerenter', stop);
      carousel.addEventListener('pointerleave', start);

      const handleViewport = () => {
        index = 0;
        render();
        window.requestAnimationFrame(measure);
        start();
      };

      const handleResize = () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(measure, 120);
      };

      mobileQuery.addEventListener?.('change', handleViewport);
      reduceMotion.addEventListener?.('change', start);
      document.addEventListener('visibilitychange', start);
      window.addEventListener('resize', handleResize, { passive: true });
      carousel.querySelectorAll('img').forEach((image) => {
        if (!image.complete) image.addEventListener('load', measure, { once: true });
      });
      if (document.fonts?.ready) document.fonts.ready.then(measure).catch(() => {});

      render();
      window.requestAnimationFrame(() => {
        measure();
        window.setTimeout(measure, 500);
      });
      start();
    });
  };

  const initialize = (scope = document) => {
    initSmartAnchors();
    initMenus(scope);
    initGalleries(scope);
    initCarousels(scope);
  };

  runWhenReady(() => initialize());
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
